<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { weeklyPlansApi, type WeeklyPlan } from '@/api/planning/weekly-plans';
import { caregiversApi, type Caregiver } from '@/api/caregivers';
import { WEEKDAY_NAMES_ES, WEEKDAYS_MONDAY_FIRST } from '@/utils/monthly-expectation';
import { careerMetadata } from '@/utils/career-metadata';
import { useTableFilter } from '@/composables/useTableFilter';
import {
  type WeeklyPlanFormValues, weeklyPlanDefaults, weeklyPlanFromPlan, toWeeklyPlanPayload,
} from './weekly-plan-form.utils';

const nameFilter = useTableFilter(['fullName']);

interface Props {
  modelValue: boolean;
  requirementId: number;
  career?: string | null;
  skillId?: number | null;
  skillName?: string | null;
  blockedCaregiverIds?: number[];
  patientGroupIds?: number[];
  durationMin?: number | null;
  weeklyPlan?: WeeklyPlan | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
  (e: 'success'): void;
}>();

const errorMessage = ref<string>('');

const MIN_WEEKDAY = 0;
const MAX_WEEKDAY = 6;
const MAX_DURATION_MIN = 1440;

const validationSchema = yup.object({
  days: yup.array()
    .of(yup.number()
      .integer()
      .min(MIN_WEEKDAY)
      .max(MAX_WEEKDAY)
      .required())
    .min(1, 'Selecciona al menos un día')
    .required(),
  durationMin: yup.number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .nullable()
    .positive('Debe ser mayor a 0')
    .integer('Debe ser un número entero')
    .max(MAX_DURATION_MIN, `Máximo ${MAX_DURATION_MIN} minutos`),
  useFixedTime: yup.boolean(),
  fixedStartTime: yup.string().nullable(),
  timeWindowStart: yup.string().nullable().when('useFixedTime', {
    is: (val: boolean) => !val,
    then: (schema) => schema.required('Define la ventana horaria'),
    otherwise: (schema) => schema.notRequired(),
  }),
  timeWindowEnd: yup.string().nullable().when('useFixedTime', {
    is: (val: boolean) => !val,
    then: (schema) => schema.required('Define la ventana horaria')
      .test('after-start', 'Debe ser posterior al inicio', (value, ctx) => (
        !value || !ctx.parent.timeWindowStart || value > ctx.parent.timeWindowStart
      )),
    otherwise: (schema) => schema.notRequired(),
  }),
  caregiverId: yup.number().nullable(),
});

const {
  handleSubmit, values, errors, defineField, resetForm,
} = useForm<WeeklyPlanFormValues>({
  validationSchema,
  initialValues: weeklyPlanDefaults(),
});

const [days] = defineField('days');
// Aliased to avoid colliding with the `durationMin` prop (the requirement's default).
const [durationField, durationFieldAttrs] = defineField('durationMin');
const [useFixedTime] = defineField('useFixedTime');
const [fixedStartTime, fixedStartTimeAttrs] = defineField('fixedStartTime');
const [timeWindowStart, timeWindowStartAttrs] = defineField('timeWindowStart');
const [timeWindowEnd, timeWindowEndAttrs] = defineField('timeWindowEnd');
const [caregiverId, caregiverIdAttrs] = defineField('caregiverId');

// Assigning a caregiver outside the patient's groups fails later at route planning
// (assignments require a shared group), so only offer group-mates upfront.
const groupParams = computed(() => (props.patientGroupIds?.length ?
  props.patientGroupIds.map((id) => `groups_id_in[]=${id}`).join('&') :
  undefined));

const { data: allCaregivers } = useQuery({
  queryKey: ['caregiversForWeeklyPlan', groupParams],
  queryFn: () => caregiversApi.index(undefined, groupParams.value),
  initialData: [] as Caregiver[],
});

const filteredCaregivers = computed(() => {
  let caregivers = allCaregivers.value;
  if (props.career) caregivers = caregivers.filter((c: Caregiver) => c.career === props.career);
  // The requirement's skill is a hard constraint: only skilled caregivers can be assigned.
  if (props.skillId) caregivers = caregivers.filter((c: Caregiver) => c.skillIds?.includes(props.skillId!));
  // Blocked caregivers can never attend the patient (enforced server-side too).
  const blocked = props.blockedCaregiverIds ?? [];
  if (blocked.length > 0) caregivers = caregivers.filter((c: Caregiver) => !blocked.includes(c.id));
  return caregivers;
});

watch(() => props.modelValue, (open) => {
  if (!open) return;
  errorMessage.value = '';
  resetForm({
    values: props.weeklyPlan ? weeklyPlanFromPlan(props.weeklyPlan) : weeklyPlanDefaults(),
  });
}, { immediate: true });

const { mutate: save, isPending } = useMutation({
  mutationFn: (form: ReturnType<typeof toWeeklyPlanPayload>) => (
    props.weeklyPlan ?
      weeklyPlansApi.update(props.weeklyPlan.id, form) :
      weeklyPlansApi.create(props.requirementId, form)
  ),
  onSuccess: () => {
    emit('success');
    emit('update:modelValue', false);
  },
  onError: (error: { response?: { data?: { errors?: string[] } } }) => {
    errorMessage.value = error?.response?.data?.errors?.join(', ') || 'Error al guardar';
  },
});

function toggleDay(day: number) {
  const current = values.days || [];
  const idx = current.indexOf(day);
  if (idx === -1) days.value = [...current, day].sort((a, b) => a - b);
  else days.value = current.filter((d) => d !== day);
}

function isDaySelected(day: number): boolean {
  return (values.days || []).includes(day);
}

const selectedDaysCount = computed(() => (values.days || []).length);

const onSubmit = handleSubmit((formValues) => {
  save(toWeeklyPlanPayload(formValues as WeeklyPlanFormValues));
});

function close() {
  emit('update:modelValue', false);
}

const careerMeta = computed(() => (props.career ? careerMetadata(props.career) : null));

const durationHint = computed(() => (
  props.durationMin ?
    `Opcional. Si lo dejas vacío, hereda la duración del requerimiento (${props.durationMin} min).` :
    'Opcional. Si lo dejas vacío, hereda la duración del requerimiento.'
));
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="540"
    persistent
    @update:model-value="close"
  >
    <v-form @submit.prevent="onSubmit">
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center ga-3 pa-4 pb-2">
          <v-icon
            icon="mdi-calendar-week"
            color="primary"
          />
          <span class="text-h6 font-weight-bold">
            {{ weeklyPlan ? 'Editar plan semanal' : 'Nuevo plan semanal' }}
          </span>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-5">
          <v-alert
            v-if="errorMessage"
            type="error"
            variant="tonal"
            closable
            class="mb-4"
            @click:close="errorMessage = ''"
          >
            {{ errorMessage }}
          </v-alert>

          <div class="mb-4">
            <label class="text-subtitle-2 font-weight-medium d-block mb-2">
              Días de la semana
            </label>
            <div class="d-flex ga-2 flex-wrap">
              <v-btn
                v-for="day in WEEKDAYS_MONDAY_FIRST"
                :key="day"
                :color="isDaySelected(day) ? 'primary' : undefined"
                :variant="isDaySelected(day) ? 'flat' : 'outlined'"
                size="small"
                min-width="48"
                class="day-chip"
                @click="toggleDay(day)"
              >
                {{ WEEKDAY_NAMES_ES[day] }}
              </v-btn>
            </div>
            <p
              v-if="errors.days"
              class="text-caption text-error mt-2 mb-0"
            >
              {{ errors.days }}
            </p>
            <p
              v-else
              class="text-caption text-medium-emphasis mt-2 mb-0"
            >
              {{ selectedDaysCount }} {{ selectedDaysCount === 1 ? 'turno' : 'turnos' }}
              por semana
            </p>
          </div>

          <v-text-field
            v-model="durationField"
            v-bind="durationFieldAttrs"
            label="Duración del turno (min)"
            type="number"
            min="1"
            variant="outlined"
            density="comfortable"
            :hint="durationHint"
            persistent-hint
            prepend-inner-icon="mdi-timer-outline"
            :error-messages="errors.durationMin"
            clearable
            class="mb-4"
          />

          <div class="mb-2">
            <label class="text-subtitle-2 font-weight-medium d-block mb-2">
              Horario
            </label>
            <v-btn-toggle
              v-model="useFixedTime"
              mandatory
              density="comfortable"
              variant="outlined"
              divided
              class="mb-3"
            >
              <v-btn
                :value="true"
                size="small"
                prepend-icon="mdi-clock-outline"
              >
                Hora fija
              </v-btn>
              <v-btn
                :value="false"
                size="small"
                prepend-icon="mdi-clock-time-four-outline"
              >
                Ventana horaria
              </v-btn>
            </v-btn-toggle>
          </div>

          <v-text-field
            v-if="useFixedTime"
            v-model="fixedStartTime"
            v-bind="fixedStartTimeAttrs"
            label="Hora fija"
            type="time"
            variant="outlined"
            density="comfortable"
            :hint="
              'Se agendará dentro de ±1 hora de esta hora. ' +
                'Déjala vacía si el turno puede ocurrir en cualquier momento de la ventana horaria de la empresa.'
            "
            persistent-hint
            prepend-inner-icon="mdi-clock-outline"
            :error-messages="errors.fixedStartTime"
            clearable
            class="mb-4"
          />

          <div
            v-else
            class="d-flex ga-3 mb-4"
          >
            <v-text-field
              v-model="timeWindowStart"
              v-bind="timeWindowStartAttrs"
              label="Desde"
              type="time"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-clock-start"
              :error-messages="errors.timeWindowStart"
            />
            <v-text-field
              v-model="timeWindowEnd"
              v-bind="timeWindowEndAttrs"
              label="Hasta"
              type="time"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-clock-end"
              :error-messages="errors.timeWindowEnd"
            />
          </div>

          <v-autocomplete
            v-model="caregiverId"
            v-bind="caregiverIdAttrs"
            :items="filteredCaregivers"
            item-title="fullName"
            item-value="id"
            :custom-filter="nameFilter"
            label="Profesional"
            variant="outlined"
            density="comfortable"
            clearable
            :hint="skillId ?
              `Opcional. Sólo se muestran profesionales con la subespecialidad ${skillName || 'requerida'}.` :
              'Opcional. Si lo asignas, se sugerirá en las rutas diarias.'"
            persistent-hint
            prepend-inner-icon="mdi-account"
            :error-messages="errors.caregiverId"
          >
            <template #chip="{ item, props: chipProps }">
              <v-chip v-bind="chipProps">
                <v-icon
                  v-if="careerMeta"
                  :icon="careerMeta.icon"
                  size="x-small"
                  class="mr-1"
                />
                {{ item.raw.fullName }}
              </v-chip>
            </template>
          </v-autocomplete>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            text="Cancelar"
            variant="text"
            @click="close"
          />
          <v-btn
            type="submit"
            color="primary"
            variant="flat"
            prepend-icon="mdi-check"
            text="Guardar"
            :loading="isPending"
          />
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<style scoped>
.day-chip {
  letter-spacing: .5px;
}
</style>
